import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import useOpenMonitoringSession from '../../hooks/useOpenMonitoringSession';
import readMonitoringAgentKeys from '../../utils/readMonitoringAgentKeys';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement, ReactNode } from 'react';
import type { IAgentSessionUnitsSkipped } from '../../interfaces/IAiChatTools';
import type IMonitoringSessionToolCall from '../../interfaces/IMonitoringSessionToolCall';

import styles from '../../sass/AiChat.module.scss';

/**
 * The `createMonitoringSession` and `updateMonitoringSessionUnits` tool calls: a monitoring
 * session of a map made, or its units changed. One component: the two answer with the same
 * shape. Neither starts the session.
 *
 * A step row: the header names the agent from `subType` and counts the units with its noun
 * ("3 products", "3 suppliers"), and the open button sits under it. Behind the toggle: the
 * template, and what the units request could not take, as counts. Unit ids are tool-side ids and
 * are never shown. A refusal is an answer for the model, not a failure: the row shows the reason
 * on one line, and the templates to pick from behind the toggle when the backend listed them.
 *
 * The session is a normal one of the agent panel — the chat does not own it. On a live part
 * `useAiChatToolActions` refreshes its cache and opens nothing; only the button opens it.
 */
function MonitoringSessionToolCall({ part }: IMonitoringSessionToolCall): ReactElement {
  const { t } = useTranslation();
  const isCreate = part.type === 'tool-createMonitoringSession';
  const label = useAiChatToolLabel(isCreate ? 'createMonitoringSession' : 'updateMonitoringSessionUnits');
  const status = useAiChatToolStatus(part.state);
  const openMonitoringSession = useOpenMonitoringSession();

  const output = part.state === 'output-available' ? part.output : undefined;

  // Under the header: which session while an update runs, or the reason of a refusal.
  const readNote = (): string | undefined => {
    if (output) {
      return output.ok ? undefined : output.reason;
    }

    return part.type === 'tool-updateMonitoringSessionUnits' && part.input?.sessionId
      ? t('aiChat.tools.agentSessionFallbackName', { id: part.input.sessionId })
      : undefined;
  };

  const readSummary = (): string | undefined => {
    if (!output) {
      return status;
    }

    if (!output.ok) {
      return t('aiChat.tools.agentSessionRefused');
    }

    const { agentKey, unitCountKey } = readMonitoringAgentKeys(output.subType);

    return t(isCreate ? 'aiChat.tools.monitoringSessionCreated' : 'aiChat.tools.monitoringSessionUpdated', {
      agent: t(agentKey),
      units: t(unitCountKey, { count: output.units.length }),
    });
  };

  // What the units request could not take, one line per kind, never an id.
  const readSkipped = ({
    unknownUnitIds, datasourceProblem, notSelectedUnitIds,
  }: IAgentSessionUnitsSkipped): string[] => [
    unknownUnitIds?.length ? t('aiChat.tools.monitoringUnknownUnits', { count: unknownUnitIds.length }) : '',
    datasourceProblem ?? '',
    notSelectedUnitIds?.length
      ? t('aiChat.tools.monitoringNotSelectedUnits', { count: notSelectedUnitIds.length })
      : '',
  ].filter(Boolean);

  const readDetails = (): ReactNode => {
    if (!output) {
      return undefined;
    }

    if (!output.ok) {
      return output.templates?.length ? (
        <>
          <p>{t('aiChat.tools.monitoringTemplateOptions')}</p>
          <ul className={styles.toolWarnings}>
            {output.templates.map(({ agentConfigId, name }) => <li key={agentConfigId}>{name}</li>)}
          </ul>
        </>
      ) : undefined;
    }

    const skipped = readSkipped(output.skipped ?? { unknownUnitIds: [] });

    if (!output.agentConfigName && !skipped.length) {
      return undefined;
    }

    return (
      <>
        {output.agentConfigName ? (
          <p>{t('aiChat.tools.monitoringTemplate', { name: output.agentConfigName })}</p>
        ) : null}
        {skipped.length ? (
          <ul className={styles.toolWarnings}>
            {skipped.map((line) => <li key={line}>{line}</li>)}
          </ul>
        ) : null}
      </>
    );
  };

  const handleOpen = () => {
    if (output?.ok) {
      openMonitoringSession({
        valueChainMapId: output.valueChainMapId, sessionId: output.sessionId, view: 'session',
      });
    }
  };

  // After a create the model points the user here.
  const openButton = output?.ok ? (
    <button type="button" className={styles.toolToggle} onClick={handleOpen}>
      {t('aiChat.tools.agentOpenSession')}
    </button>
  ) : undefined;

  return (
    <AiChatToolCard
      name={label}
      status={readSummary()}
      content={openButton}
      details={readDetails()}
      errorText={part.errorText}
    >
      {readNote()}
    </AiChatToolCard>
  );
}

export default MonitoringSessionToolCall;
